import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Search, Loader2, Hash, FileSignature, Zap, Eye, RefreshCw, Download, Users } from "lucide-react";

const SOURCE_LABELS = { pipeline: "Pipeline", preventivatore: "Preventivatore", fier: "FIER" };
const eur = (n) => Number(n || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 2 });
const kw = (n) => (n == null ? "—" : `${Number(n).toLocaleString("it-IT", { maximumFractionDigits: 2 })} kWp`);

function OrderDetail({ order, onClose }) {
  if (!order) return null;
  const c = order.cliente || {};
  const imp = order.importi || {};
  const cc = order.condizioni_commerciali || {};
  return (
    <Dialog open={!!order} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="order-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSignature className="h-5 w-5 text-primary" />
            Ordine {order.numero || "—"}
            <Badge variant="outline" className="ml-1">{SOURCE_LABELS[order.source] || order.source}</Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="grid sm:grid-cols-2 gap-4 text-sm">
          <div className="rounded-lg border border-border p-3 space-y-1">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Anagrafica</div>
            <div className="font-semibold">{c.nome || c.ragione_sociale || "—"}</div>
            {c.ragione_sociale && c.ragione_sociale !== c.nome && <div>{c.ragione_sociale}</div>}
            {c.partita_iva && <div>P.IVA {c.partita_iva}</div>}
            {c.codice_fiscale && <div>C.F. {c.codice_fiscale}</div>}
            {c.email && <div className="text-muted-foreground">{c.email}</div>}
            {c.telefono && <div className="text-muted-foreground">{c.telefono}</div>}
            {c.referente && <div className="text-muted-foreground">Rif. {c.referente}</div>}
            {c.indirizzo && <div className="text-muted-foreground">{c.indirizzo}</div>}
          </div>
          <div className="rounded-lg border border-border p-3 space-y-1">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Impianto & Importi</div>
            <div>Potenza: <b>{kw(order.impianto?.potenza_kwp)}</b></div>
            {order.impianto?.accumulo_kwh != null && <div>Accumulo: {order.impianto.accumulo_kwh} kWh</div>}
            <div className="pt-1">Imponibile: {eur(imp.imponibile)}</div>
            <div>IVA ({imp.iva_pct ?? 0}%): {eur(imp.iva)}</div>
            <div className="font-semibold text-base">Totale: {eur(imp.totale)}</div>
            <div className="text-muted-foreground pt-1">Data: {order.data_ordine || "—"} · {order.consulente || "—"}</div>
          </div>
        </div>

        {cc && (
          <div className="rounded-lg border border-border p-3 text-sm space-y-1 mt-1">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Condizioni commerciali</div>
            {cc.pagamento && <div>Pagamento: {cc.pagamento}</div>}
            {cc.chiavi_in_mano != null && <div>Chiavi in mano: {cc.chiavi_in_mano ? "Sì" : "No"}</div>}
            {cc.consegna?.data_consegna && <div>Consegna: {cc.consegna.data_consegna}</div>}
            {cc.caparra?.importo != null && <div>Caparra: {eur(cc.caparra.importo)} {cc.caparra.pagata ? "(pagata)" : ""}</div>}
            {cc.sconto?.importo != null && <div>Sconto: {eur(cc.sconto.importo)}</div>}
            {Array.isArray(cc.servizi_inclusi) && cc.servizi_inclusi.length > 0 && <div>Servizi inclusi: {cc.servizi_inclusi.join(", ")}</div>}
            {cc.note && <div className="text-muted-foreground">Note: {cc.note}</div>}
          </div>
        )}

        <div className="mt-2">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-2">Articoli e servizi ({(order.righe || []).length})</div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Descrizione</TableHead>
                <TableHead className="text-xs text-right">Q.tà</TableHead>
                <TableHead className="text-xs">U.m.</TableHead>
                <TableHead className="text-xs text-right">Prezzo</TableHead>
                <TableHead className="text-xs text-right">Totale</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(order.righe || []).map((r, i) => (
                <TableRow key={i} data-testid={`order-righe-${i}`}>
                  <TableCell className="text-sm">{r.descrizione}{r.dettaglio ? <span className="block text-xs text-muted-foreground">{r.dettaglio}</span> : null}</TableCell>
                  <TableCell className="text-sm text-right">{Number(r.quantita || 0).toLocaleString("it-IT")}</TableCell>
                  <TableCell className="text-sm">{r.um}</TableCell>
                  <TableCell className="text-sm text-right">{eur(r.prezzo_unitario)}</TableCell>
                  <TableCell className="text-sm text-right">{eur(r.totale_riga)}</TableCell>
                </TableRow>
              ))}
              {(order.righe || []).length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground text-sm py-4">Nessun articolo</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Orders() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [source, setSource] = useState("all");
  const [detail, setDetail] = useState(null);
  const [backfilling, setBackfilling] = useState(false);
  const [fierImporting, setFierImporting] = useState(false);
  const [reconciling, setReconciling] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/orders?source=${source}${q ? `&q=${encodeURIComponent(q)}` : ""}`)
      .then((r) => setOrders(r.data?.items || []))
      .catch(() => toast.error("Errore nel caricamento degli ordini"))
      .finally(() => setLoading(false));
  }, [source, q]);

  useEffect(() => { load(); }, [source]); // eslint-disable-line react-hooks/exhaustive-deps

  const runBackfill = () => {
    setBackfilling(true);
    api.post("/admin/backfill-commesse")
      .then((r) => { toast.success(`Commesse assegnate: ${r.data?.assegnate ?? 0}`); load(); })
      .catch(() => toast.error("Backfill non riuscito"))
      .finally(() => setBackfilling(false));
  };

  const pollFier = useCallback(() => {
    const t = setInterval(async () => {
      try {
        const { data } = await api.get("/fier-ordini/import/status");
        if (data?.phase === "done") { clearInterval(t); setFierImporting(false); toast.success(`FIER ordini: ${data.created} nuovi · ${data.updated} aggiornati`); load(); }
        else if (data?.phase === "error") { clearInterval(t); setFierImporting(false); toast.error(`Import FIER ordini: ${data.error || "errore"}`); }
      } catch { /* ignore */ }
    }, 3000);
  }, [load]);

  const importFier = () => {
    setFierImporting(true);
    api.post("/fier-ordini/import")
      .then(() => { toast.info("Import ordini da FIER avviato: scraping in corso (qualche minuto)."); pollFier(); })
      .catch(() => { setFierImporting(false); toast.error("Avvio import FIER non riuscito"); });
  };

  const runReconcile = () => {
    setReconciling(true);
    api.post("/admin/reconcile-clienti")
      .then((r) => { const d = r.data || {}; toast.success(`Anagrafiche: ${d.created || 0} create · collegati ${(d.linked_fier || 0) + (d.linked_leads || 0) + (d.linked_preventivi || 0)} ordini/preventivi`); load(); })
      .catch(() => toast.error("Riconciliazione non riuscita"))
      .finally(() => setReconciling(false));
  };

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="orders-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-2"><FileSignature className="h-6 w-6 text-primary" /> Ordini</h1>
          <p className="text-sm text-muted-foreground">Contratti della pipeline e proposte del Preventivatore chiuse</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={importFier} disabled={fierImporting} variant="outline" data-testid="orders-fier-import-btn">
            {fierImporting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />}
            Importa ordini da FIER
          </Button>
          {isAdmin && (
            <>
              <Button onClick={runReconcile} disabled={reconciling} variant="outline" data-testid="orders-reconcile-btn">
                {reconciling ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Users className="h-4 w-4 mr-1.5" />}
                Riconcilia anagrafiche
              </Button>
              <Button onClick={runBackfill} disabled={backfilling} variant="outline" data-testid="orders-backfill-btn">
                {backfilling ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}
                Assegna codici mancanti
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Cerca per codice, cliente, P.IVA, consulente…" className="pl-9" data-testid="orders-search" />
        </div>
        <Select value={source} onValueChange={setSource}>
          <SelectTrigger className="w-[180px]" data-testid="orders-source-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le fonti</SelectItem>
            <SelectItem value="pipeline">Pipeline</SelectItem>
            <SelectItem value="preventivatore">Preventivatore</SelectItem>
            <SelectItem value="fier">FIER</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={load} data-testid="orders-search-btn"><Search className="h-4 w-4 mr-1.5" /> Cerca</Button>
      </div>

      <Card className="overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin mr-2" /> Caricamento…</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs uppercase tracking-wider">Codice ordine</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Fonte</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Cliente</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-right">kWp</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-right">Imponibile</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-right">IVA</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-right">Totale</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Data</TableHead>
                <TableHead className="text-xs uppercase tracking-wider"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.map((o) => (
                <TableRow key={o.order_id} data-testid={`order-row-${o.order_id}`} className="hover:bg-secondary/30">
                  <TableCell className="font-mono text-sm">
                    {o.numero ? (
                      <span className="inline-flex items-center gap-1 font-semibold text-green-700"><Hash className="h-3 w-3" />{o.numero}</span>
                    ) : <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell><Badge variant="outline" className="text-xs">{SOURCE_LABELS[o.source] || o.source}</Badge></TableCell>
                  <TableCell className="text-sm">
                    <div className="font-medium">{o.cliente?.nome || o.cliente?.ragione_sociale || "—"}</div>
                    {o.cliente?.partita_iva && <div className="text-xs text-muted-foreground">P.IVA {o.cliente.partita_iva}</div>}
                  </TableCell>
                  <TableCell className="text-sm text-right whitespace-nowrap"><span className="inline-flex items-center gap-1"><Zap className="h-3 w-3 text-amber-500" />{kw(o.impianto?.potenza_kwp)}</span></TableCell>
                  <TableCell className="text-sm text-right">{eur(o.importi?.imponibile)}</TableCell>
                  <TableCell className="text-sm text-right text-muted-foreground">{eur(o.importi?.iva)}</TableCell>
                  <TableCell className="text-sm text-right font-semibold">{eur(o.importi?.totale)}</TableCell>
                  <TableCell className="text-sm whitespace-nowrap">{o.data_ordine || "—"}</TableCell>
                  <TableCell>
                    <Button size="sm" variant="ghost" onClick={() => setDetail(o)} data-testid={`order-detail-btn-${o.order_id}`}><Eye className="h-4 w-4 mr-1" /> Dettagli</Button>
                  </TableCell>
                </TableRow>
              ))}
              {orders.length === 0 && (
                <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-12">Nessun ordine trovato</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Card>

      <div className="text-xs text-muted-foreground mt-3" data-testid="orders-total">{orders.length} ordini</div>
      <OrderDetail order={detail} onClose={() => setDetail(null)} />
    </div>
  );
}
